import { Injectable, inject } from '@angular/core';
import { Observable, from } from 'rxjs';
import { ApiService } from './api.service';
import { environment } from 'src/environments/environment';
import { BaseService } from './base.service';

export type AreaResponse = {
  totalArea: number;
  protectedArea: number;
};

export type Ton = { tons: number; year: number };
export type TonResponse = { past: Ton[]; future: Ton[] };
export type Amount = { amount: number; year: number };
export type AmountResponse = { past: Amount[]; future: Amount[] };
export type Type = 'project' | 'organization';

@Injectable({
  providedIn: 'root',
})
export class ChartsService extends BaseService {
  public fetchAreas(projectId: string, chartFor: Type): Observable<AreaResponse> {
    this.validateMandatoryValues(projectId, chartFor);

    if (chartFor === 'project') {
      return from(this.API.get(environment.API_NAME, `/charts/project/areas/${projectId}`));
    } else {
      return from(this.API.get(environment.API_NAME, `/charts/areas`));
    }
  }

  public fetchCarbons(projectId: string, chartFor: Type): Observable<TonResponse> {
    this.validateMandatoryValues(projectId, chartFor);

    if (chartFor === 'project') {
      return from(this.API.get(environment.API_NAME, `/charts/project/carbon/${projectId}`));
    } else {
      return from(this.API.get(environment.API_NAME, `/charts/carbon`));
    }
  }

  public fetchAssets(projectId: string, chartFor: Type): Observable<AmountResponse> {
    this.validateMandatoryValues(projectId, chartFor);

    if (chartFor === 'project') {
      return from(this.API.get(environment.API_NAME, `/charts/project/assets/${projectId}`));
    } else {
      return from(this.API.get(environment.API_NAME, `/charts/assets`));
    }
  }

  public fetchMoney(projectId: string, chartFor: Type): Observable<AmountResponse> {
    this.validateMandatoryValues(projectId, chartFor);

    if (chartFor === 'project') {
      return from(this.API.get(environment.API_NAME, `/charts/project/money/${projectId}`));
    } else {
      return from(this.API.get(environment.API_NAME, `/charts/money`));
    }
  }

  public fetchGains(projectId: string, chartFor: Type): Observable<AmountResponse> {
    this.validateMandatoryValues(projectId, chartFor);

    if (chartFor === 'project') {
      return from(this.API.get(environment.API_NAME, `/charts/project/gains/${projectId}`));
    } else {
      return from(this.API.get(environment.API_NAME, `/charts/gains`));
    }
  }

  public fetchDamage(projectId: string, chartFor: Type): Observable<AmountResponse> {
    this.validateMandatoryValues(projectId, chartFor);

    if (chartFor === 'project') {
      return from(this.API.get(environment.API_NAME, `/charts/project/damage/${projectId}`));
    } else {
      return from(this.API.get(environment.API_NAME, `/charts/damage`));
    }
  }

  public fetchIncome(projectId: string, chartFor: Type): Observable<AmountResponse> {
    this.validateMandatoryValues(projectId, chartFor);

    if (chartFor === 'project') {
      return from(this.API.get(environment.API_NAME, `/charts/project/income/${projectId}`));
    } else {
      return from(this.API.get(environment.API_NAME, `/charts/income`));
    }
  }

  public fetchQuality(projectId: string, chartFor: Type): Observable<AmountResponse> {
    this.validateMandatoryValues(projectId, chartFor);

    if (chartFor === 'project') {
      return from(this.API.get(environment.API_NAME, `/charts/project/quality/${projectId}`));
    } else {
      return from(this.API.get(environment.API_NAME, `/charts/quality`));
    }
  }

  private validateMandatoryValues(projectId: string, chartFor: string): void {
    if (!projectId && chartFor === 'project') {
      const error = new Error(`projectId is mandatory for chart of project type`);

      this.logger.error({ message: $localize`projectId is mandatory for chart of project type`, error });
      throw error;
    }
  }

  private readonly API = inject(ApiService);
}
